import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  type LlmChatMessageDto,
  type LlmChatMessageUiAction,
  type LlmChatSessionDto,
} from '@paladin-monorepo/common';
import { useQueryClient } from '@tanstack/react-query';
import { llmChatApi } from '@/lib/api/llm-chat';
import { getHttpErrorStatus, isNotFoundError, isPlanLimitExceededError } from '@/lib/httpErrorMessage';
import { useLanguage } from '@/hooks/useLanguage';
import { userTurnHasAssistantTextReply } from '@/lib/llm-chat-reply';

type LlmChatSessionQueryData = { session: LlmChatSessionDto; messages: LlmChatMessageDto[] };

export interface StreamingToolEvent {
  name: string;
  status: 'running' | 'finished';
}

interface UseLlmChatStreamReturn {
  streamingContent: string;
  streamingUiAction: LlmChatMessageUiAction | null;
  /** Tool executions observed during the current stream, in order of occurrence. */
  streamingTools: StreamingToolEvent[];
  isStreaming: boolean;
  error: string | null;
  /** Message rejected because the plan's monthly AI budget is used up. */
  planLimitExceeded: boolean;
  /** Set when the stream ended without a persisted assistant reply (retry may help). */
  streamEndedIncomplete: boolean;
  sendAndStream: (sessionId: string, content: string, attachmentIds?: string[]) => Promise<void>;
  /** Re-open SSE for the latest user message without posting another user message (e.g. after a failed stream). */
  resumeStream: (sessionId: string) => void;
}

const errorTranslationKeys: Record<string, string> = {
  llm_rate_limit: 'llmChat.error.rateLimitReached',
  llm_busy: 'llmChat.error.serviceBusy',
  llm_billing: 'llmChat.error.billingUnavailable',
  llm_context_too_long: 'llmChat.error.contextTooLong',
  llm_content_blocked: 'llmChat.error.contentBlocked',
  llm_session_unavailable: 'llmChat.error.sessionUnavailable',
  llm_legacy_office_conversion_failed: 'llmChat.error.legacyOfficeConversionFailed',
  llm_attachment_failed: 'llmChat.error.attachmentFailed',
  llm_error: 'llmChat.error.generic',
};

const AUTO_RETRY_CODES = new Set(['connection_lost', 'llm_busy']);

// The BE sends a ping every 15s, so silence for longer than this means the stream is really stalled.
const STREAM_INACTIVITY_MS = 45_000;

function reportLlmStreamFailure(
  reason: string,
  sessionId: string,
  extra?: Record<string, unknown>,
): void {
  Sentry.captureMessage(`LLM stream failure: ${reason}`, {
    level: 'error',
    tags: { feature: 'llm-chat-stream' },
    extra: { sessionId, ...extra },
  });
}

export function useLlmChatStream(): UseLlmChatStreamReturn {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingUiAction, setStreamingUiAction] = useState<LlmChatMessageUiAction | null>(null);
  const [streamingTools, setStreamingTools] = useState<StreamingToolEvent[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planLimitExceeded, setPlanLimitExceeded] = useState(false);
  const [streamEndedIncomplete, setStreamEndedIncomplete] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const streamCompletedRef = useRef(false);
  const autoRetriedRef = useRef(false);
  const activeSessionIdRef = useRef<string | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTextRef = useRef('');
  const flushFrameRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  // One state update per frame instead of per token keeps long answers smooth.
  const flushPendingText = useCallback(() => {
    if (flushFrameRef.current != null) cancelAnimationFrame(flushFrameRef.current);
    flushFrameRef.current = null;
    const text = pendingTextRef.current;
    pendingTextRef.current = '';
    if (text) setStreamingContent((prev) => prev + text);
  }, []);

  const resetStreamingOutput = useCallback(() => {
    if (flushFrameRef.current != null) cancelAnimationFrame(flushFrameRef.current);
    flushFrameRef.current = null;
    pendingTextRef.current = '';
    setStreamingContent('');
    setStreamingUiAction(null);
    setStreamingTools([]);
  }, []);

  const attachEventSource = useCallback(
    (sessionId: string, options?: { isAutoRetry?: boolean }) => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      clearWatchdog();
      streamCompletedRef.current = false;
      activeSessionIdRef.current = sessionId;
      if (!options?.isAutoRetry) {
        autoRetriedRef.current = false;
      }

      const url = llmChatApi.getStreamUrl(sessionId, language);
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;
      let hasOpened = false;

      const armWatchdog = () => {
        clearWatchdog();
        watchdogRef.current = setTimeout(
          () => handleStreamFailure('connection_lost', 'llmChat.error.connectionLost', { reason: 'inactivity' }),
          STREAM_INACTIVITY_MS,
        );
      };

      const handleStreamFailure = (
        code: string,
        messageKey: string,
        extra?: Record<string, unknown>,
      ) => {
        eventSource.close();
        eventSourceRef.current = null;
        clearWatchdog();
        flushPendingText();
        setIsStreaming(false);
        setStreamEndedIncomplete(true);
        setError(t(messageKey));

        reportLlmStreamFailure(code, sessionId, { messageKey, ...extra });

        if (
          AUTO_RETRY_CODES.has(code) &&
          !autoRetriedRef.current &&
          activeSessionIdRef.current === sessionId
        ) {
          autoRetriedRef.current = true;
          // The BE regenerates from scratch on a new connection, so drop the partial text.
          resetStreamingOutput();
          setError(null);
          setIsStreaming(true);
          attachEventSource(sessionId, { isAutoRetry: true });
        }
      };

      // EventSource reconnects on its own and the BE then regenerates from scratch.
      eventSource.onopen = () => {
        if (hasOpened) resetStreamingOutput();
        hasOpened = true;
        armWatchdog();
      };

      eventSource.onmessage = (event) => {
        armWatchdog();
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'text') {
            pendingTextRef.current += data.content;
            flushFrameRef.current ??= requestAnimationFrame(flushPendingText);
          } else if (data.type === 'tool_event' && typeof data.name === 'string') {
            if (data.status === 'started') {
              setStreamingTools((prev) => [...prev, { name: data.name, status: 'running' }]);
            } else if (data.status === 'finished') {
              setStreamingTools((prev) => {
                let lastRunningIdx = -1;
                for (let i = prev.length - 1; i >= 0; i--) {
                  if (prev[i].name === data.name && prev[i].status === 'running') {
                    lastRunningIdx = i;
                    break;
                  }
                }
                if (lastRunningIdx === -1) return prev;
                return prev.map((tool, idx) =>
                  idx === lastRunningIdx ? { ...tool, status: 'finished' as const } : tool,
                );
              });
            }
          } else if (data.type === 'error') {
            const code = typeof data.code === 'string' ? data.code : 'llm_error';
            const key = errorTranslationKeys[code] ?? 'llmChat.error.generic';
            handleStreamFailure(code, key, {
              backendMessage: typeof data.message === 'string' ? data.message : undefined,
            });
          } else if (data.type === 'ui_action' && data.uiAction) {
            setStreamingUiAction(data.uiAction);
          } else if (data.type === 'done') {
            streamCompletedRef.current = true;
            clearWatchdog();
            flushPendingText();
            eventSource.close();
            eventSourceRef.current = null;
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['subscription-usage'] });
            void (async () => {
              try {
                await queryClient.cancelQueries({ queryKey: ['decision-case-by-session'] });
                await queryClient.cancelQueries({ queryKey: ['decision-case'] });
                await Promise.all([
                  queryClient.refetchQueries({ queryKey: ['llm-chat-session', sessionId] }),
                  queryClient.refetchQueries({ queryKey: ['decision-case-by-session'] }),
                  queryClient.refetchQueries({ queryKey: ['decision-case'] }),
                ]);
                const sessionData = queryClient.getQueryData<LlmChatSessionQueryData>([
                  'llm-chat-session',
                  sessionId,
                ]);
                const missingReply =
                  sessionData != null && !userTurnHasAssistantTextReply(sessionData.messages);
                setStreamEndedIncomplete(missingReply);
                if (!missingReply) {
                  setError(null);
                }
              } finally {
                setStreamingContent('');
                setStreamingUiAction(null);
                setStreamingTools([]);
                setIsStreaming(false);
              }
            })();
          } else if (data.type === 'resource_updated') {
            if (data.resourceType === 'inquiry' && data.resourceId) {
              void queryClient.invalidateQueries({ queryKey: ['inquiry', data.resourceId] });
            } else if (data.resourceType === 'decision_case' && data.resourceId) {
              void queryClient.invalidateQueries({ queryKey: ['decision-case', data.resourceId] });
              void queryClient.invalidateQueries({ queryKey: ['decision-case-by-session'] });
            }
          }
        } catch {
          // ignore parse errors
        }
      };

      eventSource.onerror = () => {
        // CONNECTING means the browser is already reconnecting; the watchdog covers a reconnect that never succeeds.
        if (streamCompletedRef.current || eventSource.readyState === EventSource.CONNECTING) {
          return;
        }
        handleStreamFailure('connection_lost', 'llmChat.error.connectionLost');
      };
    },
    [language, queryClient, t, clearWatchdog, flushPendingText, resetStreamingOutput],
  );

  const resumeStream = useCallback(
    (sessionId: string) => {
      setStreamingContent('');
      setStreamingUiAction(null);
      setStreamingTools([]);
      setError(null);
      setPlanLimitExceeded(false);
      setStreamEndedIncomplete(false);
      setIsStreaming(true);
      attachEventSource(sessionId);
    },
    [attachEventSource],
  );

  const sendAndStream = useCallback(
    async (sessionId: string, content: string, attachmentIds?: string[]) => {
      setStreamingContent('');
      setStreamingUiAction(null);
      setStreamingTools([]);
      setError(null);
      setPlanLimitExceeded(false);
      setStreamEndedIncomplete(false);
      setIsStreaming(true);

      try {
        const message = await llmChatApi.sendMessage(sessionId, { content, attachmentIds });

        queryClient.setQueryData<LlmChatSessionQueryData>(['llm-chat-session', sessionId], (old) => {
          if (!old || old.messages.some((m) => m.id === message.id)) {
            return old;
          }
          return {
            ...old,
            messages: [...old.messages, message],
          };
        });
        void queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] });

        attachEventSource(sessionId);
      } catch (err) {
        setIsStreaming(false);
        Sentry.captureException(err, {
          extra: { sessionId, source: 'llm-chat.send-message' },
        });
        if (isPlanLimitExceededError(err)) {
          setPlanLimitExceeded(true);
          setError(t('llmChat.error.planLimitExceeded'));
        } else if (getHttpErrorStatus(err) === 503) {
          setError(t('llmChat.error.serviceBusy'));
        } else if (isNotFoundError(err) || getHttpErrorStatus(err) === 403) {
          setError(t('llmChat.error.sessionUnavailable'));
        } else {
          setError(t('llmChat.error.sendFailed'));
        }
      }
    },
    [attachEventSource, queryClient, t],
  );

  return {
    streamingContent,
    streamingUiAction,
    streamingTools,
    isStreaming,
    error,
    planLimitExceeded,
    streamEndedIncomplete,
    sendAndStream,
    resumeStream,
  };
}
